/**
 * Sharing: who outside the team sees which screens of the founder's app (docs/architecture.md, "Sharing and access").
 * One card per screen the app lists (drafts never show): the people it's shared with, a form to share it with an
 * email, and the Share with Listen switch, off by default, in the privacy policy's own words. Only the founder
 * shares; teammates see it all and may stop a share. Built from the library only.
 */
import { useState, type FormEvent } from 'react'
import { DEFAULT_SHARE_FOR, emailProblem, LISTEN_NEVER, LISTEN_OFF, LISTEN_ON, personLine, PRIVACY_URL, screenShares, SHARE_FOR, shareDays, sourceNote, type ScreenShares, type ShareFor } from '../../domain/sharing'
import type { Grant, GrantList, ShareScreens } from '../../domain/schemas'
import type { Api } from '../../infra/api'
import { Button, Card, Chip, ConfirmAction, DataTable, EmptyState, ErrorNote, Inline, Input, Screen, Segmented, Stack, Text, type Query } from '../../ui'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'
import { useFounder } from './context'

interface Loaded {
  screens: ShareScreens
  grants: GrantList
}

export function SharingPage() {
  const f = useFounder()
  return <Sharing api={f.api} company={f.state.world.founder.name} />
}

/** The page on any API client, so tests can mount it on the mock. */
export function Sharing({ api, company }: { api: Api; company: string }) {
  const screens = useLoad(() => api.shareScreens(), [api])
  const grants = useLoad(() => api.grants(), [api])
  const query: Query<Loaded> = {
    data: screens.data && grants.data ? { screens: screens.data, grants: grants.data } : undefined,
    error: screens.error ?? grants.error,
    loading: screens.loading || grants.loading,
    reload: () => (screens.reload(), grants.reload()),
  }
  return (
    <Screen
      title="Sharing"
      description={`Choose who outside ${company} sees each screen of your app. They sign in and see only those screens, never your code, your computer or the rest of your data.`}
      query={query}
      skeleton="cards"
    >
      {(d) => <Shares data={d} api={api} reload={grants.reload} />}
    </Screen>
  )
}

function Shares({ data, api, reload }: { data: Loaded; api: Api; reload: () => void }) {
  const now = useNow(60_000)
  const rows = screenShares(data.screens.screens, data.grants.grants)
  const note = sourceNote(data.screens, now)
  return (
    <Stack gap="lg">
      {note && <Text tone="muted" size="sm">{note}</Text>}
      {!data.grants.can.share && <Text tone="muted" size="sm">Only the founder shares screens. You can see who has access and stop a share.</Text>}
      {rows.length === 0 ? (
        <EmptyState title="No screens to share yet.">
          Your app's screens show up here once it's live. New screens start as drafts that only your team sees; ask your agent to make one ready to share, then ship it.
        </EmptyState>
      ) : (
        rows.map((r) => <ScreenCard key={r.screen.path} row={r} can={data.grants.can} api={api} reload={reload} now={now} />)
      )}
    </Stack>
  )
}

function ScreenCard({ row, can, api, reload, now }: { row: ScreenShares; can: GrantList['can']; api: Api; reload: () => void; now: number }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  async function run(key: string, act: () => Promise<unknown>) {
    setBusy(key)
    setFailed(null)
    try {
      await act()
      reload()
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const people = row.people
  return (
    <Card
      title={row.screen.title}
      count={people.length || undefined}
      action={can.share && row.listed && !adding ? <Button size="sm" icon="plus" onClick={() => setAdding(true)}>Share with a person</Button> : undefined}
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Text mono size="xs" tone="muted">{row.screen.path}</Text>
          {row.screen.description && <Text tone="muted" size="sm">{row.screen.description}</Text>}
          {!row.listed && <Text tone="down" size="sm">Your app no longer lists this screen. Stop these shares, or bring the screen back.</Text>}
        </Stack>

        {adding && (
          <ShareForm
            screen={row.screen.title}
            onCancel={() => setAdding(false)}
            onShare={(email, days) =>
              run('add', async () => {
                await api.createGrant({ screen: row.screen.path, email, expiresInDays: days })
                setAdding(false)
              })
            }
            working={busy === 'add'}
          />
        )}

        <DataTable<Grant>
          label={`People who see ${row.screen.title}`}
          rows={people}
          rowKey={(g) => g.id}
          framed={false}
          empty={<Text tone="muted" size="sm">Not shared with anyone outside your team.</Text>}
          columns={[
            {
              key: 'who',
              label: 'Person',
              narrow: 'main',
              render: (g) => (
                <Stack gap="none">
                  <Text weight="head">{g.grantee.kind === 'person' ? (g.grantee.name ?? g.grantee.email) : g.grantee.label}</Text>
                  {g.grantee.kind === 'person' && g.grantee.name && <Text mono size="xs" tone="muted">{g.grantee.email}</Text>}
                </Stack>
              ),
            },
            { key: 'state', label: 'Status', narrow: 'end', render: (g) => (g.status === 'expired' ? <Chip tone="mute">ended</Chip> : <Chip>sharing</Chip>) },
            { key: 'when', label: 'Access', narrow: 'below', render: (g) => <Text size="sm" tone="muted">{personLine(g, now)}</Text> },
            {
              key: 'stop',
              label: 'Actions',
              align: 'end',
              narrow: 'below',
              narrowLabel: false,
              render: (g) =>
                can.revoke ? (
                  <ConfirmAction
                    label={g.status === 'expired' ? 'Remove' : 'Stop sharing'}
                    strength="confirm"
                    variant="ghost"
                    question={`${g.grantee.kind === 'person' ? g.grantee.email : 'They'} stop seeing ${row.screen.title} within a minute.`}
                    yes="Stop sharing"
                    onConfirm={() => void run(g.id, () => api.revokeGrant(g.id))}
                    working={busy === g.id}
                    disabled={busy !== null}
                  />
                ) : null,
            },
          ]}
        />

        <ListenSwitch row={row} can={can} busy={busy === 'listen'} onTurn={(on) => run('listen', () => (on ? api.createGrant({ screen: row.screen.path, group: 'listen', expiresInDays: null }) : api.revokeGrant(row.listen!.id)))} />

        {failed && <ErrorNote title="That didn't go through." reason={failed} />}
      </Stack>
    </Card>
  )
}

/** Share with Listen: per screen, off by default, the policy's words for whichever way it is. */
function ListenSwitch({ row, can, busy, onTurn }: { row: ScreenShares; can: GrantList['can']; busy: boolean; onTurn: (on: boolean) => void }) {
  const on = row.listen !== null
  const mayTurn = on ? can.revoke : can.share && row.listed
  return (
    <Stack gap="xs">
      <Inline gap="sm" align="center" justify="between" wrap>
        <Text weight="head" size="base">Share with Listen</Text>
        {mayTurn && !busy ? (
          <Segmented<'off' | 'on'>
            label={`Share ${row.screen.title} with Listen`}
            size="sm"
            value={on ? 'on' : 'off'}
            options={[
              { value: 'off', label: 'Off' },
              { value: 'on', label: 'On' },
            ]}
            onChange={(v) => v !== (on ? 'on' : 'off') && onTurn(v === 'on')}
          />
        ) : (
          <Chip>{busy ? 'saving…' : on ? 'on' : 'off'}</Chip>
        )}
      </Inline>
      <Text tone="muted" size="sm">
        {on ? LISTEN_ON : LISTEN_OFF} {LISTEN_NEVER}{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
          Read the privacy policy
        </a>
        .
      </Text>
    </Stack>
  )
}

function ShareForm({ screen, onShare, onCancel, working }: { screen: string; onShare: (email: string, days: number | null) => void; onCancel: () => void; working: boolean }) {
  const [email, setEmail] = useState('')
  const [howLong, setHowLong] = useState<ShareFor>(DEFAULT_SHARE_FOR)
  const [touched, setTouched] = useState(false)
  const problem = emailProblem(email)

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!problem) onShare(email.trim(), shareDays(howLong))
  }

  return (
    <form onSubmit={submit} noValidate aria-label={`Share ${screen} with a person`}>
      <Stack gap="sm">
        <Input type="email" aria-label="Their email" placeholder="name@example.com" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        {touched && problem && <Text tone="down" size="sm">{problem}</Text>}
        <Inline gap="sm" align="center" wrap>
          <Text size="sm" tone="muted">For</Text>
          <Segmented<ShareFor> label="How long" size="sm" value={howLong} onChange={setHowLong} options={SHARE_FOR.map((o) => ({ value: o.value, label: o.label }))} />
        </Inline>
        <Text size="sm" tone="muted">
          They sign in at app.cpg.computer with this email and see {screen} only. We don't send an email, so tell them it's there.
        </Text>
        <Inline gap="sm">
          <Button type="submit" variant="ink" size="sm" disabled={working}>
            {working ? 'Sharing…' : 'Share'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}
